/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { Link } from '@client/components';

/*----------------------------------
- TYPES
----------------------------------*/

export type SidebarRailTone = 'default' | 'accent' | 'danger';

export interface SidebarRailItemProps {
    label: string;
    icon: React.ReactNode;
    href?: string;
    onClick?: () => void;
    active?: boolean;
    disabled?: boolean;
    tone?: SidebarRailTone;
    dataTestId?: string;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TONE_CLASSNAMES: Record<SidebarRailTone, { idle: string; active: string }> = {
    default: {
        idle: 'text-slate-400 hover:bg-primary-50/80 hover:text-primary-600 hover:ring-primary-100/80',
        active: 'bg-primary-50 text-primary-700 shadow-sm shadow-primary-100/60 ring-primary-100',
    },
    accent: {
        idle: 'text-orange-400 hover:bg-orange-50 hover:text-orange-600 hover:ring-orange-100',
        active: 'bg-orange-50 text-orange-600 shadow-sm shadow-orange-100/60 ring-orange-100',
    },
    danger: {
        idle: 'text-rose-400 hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-100',
        active: 'bg-rose-50 text-rose-600 shadow-sm shadow-rose-100/60 ring-rose-100',
    },
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/** Hairline that stands in for a section header once the rail has no room for labels. */
export const SidebarRailDivider = () => (
    <div className="my-1.5 flex justify-center" aria-hidden="true">
        <span className="h-px w-7 rounded-full bg-linear-to-r from-transparent via-slate-200 to-transparent" />
    </div>
);

/**
 * One icon slot on the collapsed sidebar. The label rides along as a fixed-position tooltip so the
 * scroll container can't clip it.
 */
export const SidebarRailItem: React.FC<SidebarRailItemProps> = ({
    label,
    icon,
    href,
    onClick,
    active = false,
    disabled = false,
    tone = 'default',
    dataTestId,
}) => {
    const [tip, setTip] = React.useState<{ top: number; left: number } | null>(null);
    const slotRef = React.useRef<HTMLDivElement | null>(null);
    const tones = TONE_CLASSNAMES[tone];

    const showTip = () => {
        const rect = slotRef.current?.getBoundingClientRect();
        if (rect) setTip({ top: rect.top + rect.height / 2, left: rect.right + 10 });
    };
    const hideTip = () => setTip(null);

    // The tooltip is viewport-positioned, so scrolling the rail would otherwise leave it behind.
    React.useEffect(() => {
        if (!tip) return;
        window.addEventListener('scroll', hideTip, true);
        window.addEventListener('resize', hideTip);
        return () => {
            window.removeEventListener('scroll', hideTip, true);
            window.removeEventListener('resize', hideTip);
        };
    }, [tip]);

    const className = [
        'relative flex h-11 w-11 items-center justify-center rounded-xl ring-1 ring-transparent transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300',
        '[&>svg]:h-[18px] [&>svg]:w-[18px]',
        disabled ? 'cursor-not-allowed text-slate-300 opacity-60' : active ? tones.active : tones.idle,
    ].join(' ');

    const content = <span className="pointer-events-none flex items-center justify-center">{icon}</span>;

    return (
        <div
            ref={slotRef}
            className="relative flex justify-center"
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
            onFocusCapture={showTip}
            onBlurCapture={hideTip}
        >
            {active && !disabled && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-gradient-primary"
                />
            )}

            {disabled ? (
                <div className={className} aria-disabled="true" data-testid={dataTestId}>
                    {content}
                </div>
            ) : href ? (
                <Link
                    to={href}
                    className={className}
                    aria-label={label}
                    aria-current={active ? 'page' : undefined}
                    data-testid={dataTestId}
                    data-active={active ? 'true' : 'false'}
                >
                    {content}
                </Link>
            ) : (
                <button type="button" onClick={onClick} className={className} aria-label={label} data-testid={dataTestId}>
                    {content}
                </button>
            )}

            {tip && (
                <span
                    role="tooltip"
                    style={{ top: `${tip.top}px`, left: `${tip.left}px` }}
                    className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-[0_10px_30px_rgba(15,23,42,0.28)]"
                >
                    {label}
                </span>
            )}
        </div>
    );
};
